import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Facebook, Instagram, Youtube, Linkedin, MapPin, Phone, Mail } from "lucide-react";
import { navLinks, licenses, locations } from "../../data/mock";
import { openContactModal } from "../../lib/contactBus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      try { localStorage.setItem("edge_newsletter_email", email); } catch (_) {}
      setSubmitted(true);
      setEmail("");
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) setError("This email is already subscribed. Thanks!");
      else setError(typeof detail === "string" ? detail : "Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[var(--slate-950)] text-[var(--off-white)] relative">
      {/* CTA strip */}
      <div className="border-b border-[var(--slate-700)]">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="section-tag !text-[var(--terracotta-light)] mb-4">// LET'S BUILD</p>
            <h2 className="font-display text-[64px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight uppercase">
              Your project,<br /><span className="text-[var(--terracotta)]">our promise.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[14px] leading-7 text-[var(--slate-300)] max-w-md">
              One dedicated team, one transparent budget, zero surprises. From first walkthrough to final reveal—Edge Developer, LLC takes ownership of every nail, every plank, every promise.
            </p>
            <button onClick={openContactModal} className="btn-terracotta mt-8">
              Start Your Project <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Newsletter */}
      <div className="border-b border-[var(--slate-700)]">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="section-tag !text-[var(--terracotta-light)] mb-3">// FIELD NOTES</p>
            <h3 className="font-display text-[28px] sm:text-[34px] uppercase tracking-tight">Subscribe to our newsletter</h3>
            <p className="text-[13.5px] text-[var(--slate-300)] mt-2 max-w-md">Renovation insights, design references, and project reveals — routed straight to your inbox.</p>
          </div>
          <form onSubmit={handleSubmit} className="flex w-full">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-transparent border border-[var(--slate-700)] px-4 py-3.5 text-[13.5px] text-[var(--off-white)] placeholder:text-[var(--slate-500)] focus:outline-none focus:border-[var(--terracotta)] transition-colors"
            />
            <button type="submit" disabled={loading} aria-label="subscribe"
              className="px-5 bg-[var(--terracotta)] hover:bg-[var(--terracotta-dark)] transition-colors disabled:opacity-60"
            >
              <ArrowRight size={18} />
            </button>
          </form>
        </div>
        {submitted && (
          <p className="max-w-[1480px] mx-auto px-6 lg:px-10 pb-4 text-[12px] text-[#a9d6b7]">
            Thanks! You're on the list — confirmation routed to info@edge-developerllc.com.
          </p>
        )}
        {error && (
          <p className="max-w-[1480px] mx-auto px-6 lg:px-10 pb-4 text-[12px] text-[#e2b3ad]">{error}</p>
        )}
      </div>

      {/* Main grid */}
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
        {/* Brand */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-[var(--terracotta)] flex items-center justify-center">
              <span className="font-display text-[var(--off-white)] text-[24px]">E</span>
            </div>
            <div>
              <div className="font-display text-[26px] uppercase tracking-wide">Edge Developer</div>
              <div className="font-mono-sans text-[10px] text-[var(--terracotta-light)] mt-0.5">LLC — EST. 2014</div>
            </div>
          </div>
          <p className="text-[13.5px] text-[var(--slate-300)] mt-6 max-w-sm leading-7">
            Full-service home renovation contractors with a single mission: deliver on time, on budget, every time.
          </p>
          <div className="flex items-center gap-4 mt-8">
            <a href="#fb" className="w-9 h-9 border border-[var(--slate-700)] flex items-center justify-center hover:bg-[var(--terracotta)] hover:border-[var(--terracotta)] transition-colors"><Facebook size={15} /></a>
            <a href="#ig" className="w-9 h-9 border border-[var(--slate-700)] flex items-center justify-center hover:bg-[var(--terracotta)] hover:border-[var(--terracotta)] transition-colors"><Instagram size={15} /></a>
            <a href="#yt" className="w-9 h-9 border border-[var(--slate-700)] flex items-center justify-center hover:bg-[var(--terracotta)] hover:border-[var(--terracotta)] transition-colors"><Youtube size={15} /></a>
            <a href="#li" className="w-9 h-9 border border-[var(--slate-700)] flex items-center justify-center hover:bg-[var(--terracotta)] hover:border-[var(--terracotta)] transition-colors"><Linkedin size={15} /></a>
          </div>
        </div>

        {/* Sitemap */}
        <div className="lg:col-span-2">
          <h4 className="section-tag mb-5">// SITEMAP</h4>
          <ul className="space-y-3">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-[13.5px] text-[var(--slate-300)] hover:text-[var(--off-white)] transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Offices */}
        <div className="lg:col-span-3">
          <h4 className="section-tag mb-5">// OFFICES</h4>
          <ul className="space-y-4">
            {locations.slice(0, 3).map((l) => (
              <li key={l.slug} className="text-[12.5px] text-[var(--slate-300)] leading-6">
                <div className="font-mono-sans text-[10px] text-[var(--terracotta-light)] mb-1">{l.name.toUpperCase()}</div>
                <div className="flex items-start gap-2"><MapPin size={11} className="mt-1 shrink-0" /> {l.address}</div>
                <div className="flex items-start gap-2 mt-0.5"><Phone size={11} className="mt-1 shrink-0" /> {l.phone}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Licenses */}
        <div className="lg:col-span-3">
          <h4 className="section-tag mb-5">// LICENSE NUMBERS</h4>
          <ul className="space-y-3">
            {licenses.map((l) => (
              <li key={l.state}>
                <div className="font-mono-sans text-[10px] text-[var(--off-white)] font-semibold">{l.state.toUpperCase()}</div>
                <div className="text-[12px] text-[var(--slate-300)] font-mono-sans tracking-wider">{l.number}</div>
              </li>
            ))}
          </ul>
          <a href="mailto:info@edge-developerllc.com" className="mt-6 inline-flex items-center gap-2 text-[12.5px] text-[var(--terracotta-light)] hover:text-[var(--off-white)] transition-colors">
            <Mail size={13} /> info@edge-developerllc.com
          </a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-[var(--slate-700)]">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[11.5px] text-[var(--slate-500)] font-mono-sans tracking-wider">
            © {new Date().getFullYear()} EDGE DEVELOPER, LLC — ALL RIGHTS RESERVED
          </p>
          <div className="flex gap-6 text-[11.5px] text-[var(--slate-500)] font-mono-sans tracking-wider">
            <a href="#privacy" className="hover:text-[var(--off-white)]">PRIVACY</a>
            <a href="#terms" className="hover:text-[var(--off-white)]">TERMS</a>
            <a href="#accessibility" className="hover:text-[var(--off-white)]">ACCESSIBILITY</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
